import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Box, List, ListItem, ListItemIcon, ListItemText, Collapse, Typography } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { getSideConfig } from './NavConfig';
import useProviderNav from './hooks/useProviderNav';

// ----------------------------------------------------------------------

const SidebarNavigation = () => {
  const { providerNavItems, hasExpiredBookings } = useProviderNav();
  const sideConfig = getSideConfig(providerNavItems, hasExpiredBookings);
  const [openItems, setOpenItems] = useState({ Customer: true });
  const location = useLocation();

  const handleToggle = (title) => {
    setOpenItems((prevOpenItems) => ({
      ...prevOpenItems,
      [title]: !prevOpenItems[title],
    }));
  };

  const renderNavItems = (items) =>
    items.map((item) => {
      const isOpen = openItems[item.title];
      const hasChildren = item.children && item.children.length > 0;

      const isActive = hasChildren && item.children.some((child) => location.pathname.includes(child.path));

      return (
        <Box key={item.title} sx={{ ml: 2, mr: 2 }}>
          <ListItem
            button
            component={hasChildren ? 'div' : NavLink}
            to={hasChildren ? undefined : item.path}
            onClick={() => hasChildren && handleToggle(item.title)}
            sx={{
              mt: 2,
              backgroundColor: isActive ? 'primary.main' : 'inherit',
              color: isActive ? 'white' : 'primary.main',
              border: isActive ? 'none' : '2px solid',
              borderColor: 'primary.main',
              '&:hover': {
                backgroundColor: isActive ? 'primary.main' : 'action.hover',
                borderRadius: 1,
              },
              borderRadius: 1,
              py: { xs: 1, sm: 2 }, // Responsive vertical padding
            }}
            key={item.title}
          >
            <ListItemIcon sx={{ color: isActive ? 'white' : 'primary.main' }} key={`icon-${item.title}`}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.title}
              primaryTypographyProps={{
                sx: {
                  fontWeight: 'bold',
                },
              }}
              key={`text-${item.title}`}
            />
            {hasChildren &&
              (isOpen ? (
                <ExpandLess sx={{ color: isActive ? 'white' : 'primary.main' }} key={`expandLess-${item.title}`} />
              ) : (
                <ExpandMore sx={{ color: isActive ? 'white' : 'primary.main' }} key={`expandMore-${item.title}`} />
              ))}
          </ListItem>
          {hasChildren && (
            <Collapse in={isOpen} timeout="auto" unmountOnExit key={`collapse-${item.title}`}>
              <List component="div" disablePadding>
                {item.children.map((child) => {
                  const isChildActive = location.pathname.includes(child.path);

                  return (
                    <ListItem
                      button
                      key={child.title}
                      component={NavLink}
                      to={child.path}
                      sx={{ pl: 4, mt: 2, color: isChildActive ? 'primary.main' : 'inherit', position: 'relative' }}
                    >
                      <ListItemIcon sx={{ color: 'primary.main' }} key={`icon-${child.title}`}>
                        {child.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {child.title}
                            {child.hasExpiredBookings && (
                              <Box
                                component="span"
                                sx={{
                                  ml: 0.5,
                                  mb: 1,
                                  width: 10,
                                  height: 10,
                                  bgcolor: 'error.main',
                                  borderRadius: '50%',
                                  display: 'inline-block',
                                }}
                              />
                            )}
                          </Box>
                        }
                        primaryTypographyProps={{
                          sx: {
                            color: isChildActive ? 'primary.main' : 'inherit',
                            fontWeight: isChildActive ? 'bold' : 'normal',
                          },
                        }}
                        key={`text-${child.title}`}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Collapse>
          )}
        </Box>
      );
    });

  return (
    <Box
      sx={{
        width: '100%',
        height: '100vh',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflow: 'auto',
      }}
    >
      <List sx={{ width: '100%' }}>
        {sideConfig.map((group, key) => (
          <Box key={group.title + key.toString()} sx={{ width: '100%' }}>
            <Typography variant="h6" sx={{ pl: 2, pt: 2, textAlign: 'center', fontWeight: 'bold' }}>
              {group.title}
            </Typography>
            {renderNavItems(group.items)}
          </Box>
        ))}
      </List>
    </Box>
  );
};

export default SidebarNavigation;
